import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { request_linkProductDetail } from '@/src/request/salesChannel/product';
import { EnumSalesChannelMultiLanguage } from '@/src/types/server/params';

interface ProductDetailData {
  products: Array<any>;
  differentCurrency?: boolean;
  exchangeRate?: number;
  fixedRate?: boolean;
  flashSaleProducts?: Array<any>;
  subscriptionBuyProducts?: Array<any>;
  salesChannelProduct?: Record<string, any>;
  aliases?: Array<any>;
}

interface SalesChannelProductDetailState {
  data: ProductDetailData | null;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  currentSalesChannelProductUuid: string | null | undefined;
  currentLanguage: string | null;
}

const initialState: SalesChannelProductDetailState = {
  data: null,
  loading: 'idle',
  error: null,
  currentSalesChannelProductUuid: undefined,
  currentLanguage: null,
};

export const fetchSalesChannelProductDetail = createAsyncThunk<
  ProductDetailData,
  {
    saleChannelUuid: string;
    salesChannelProductUuid: string;
    forceUpdate?: boolean;
    langParams?: Record<string, unknown>;
    currentLanguage?: string;
  },
  {
    // ThunkAPI
    rejectValue: string;
    state: { salesChannelProductDetail: SalesChannelProductDetailState };
  }
>(
  'salesChannelProductDetail/fetchDetail',
  async (
    {
      saleChannelUuid,
      salesChannelProductUuid,
      forceUpdate = false,
      langParams = {},
    },
    { getState, rejectWithValue },
  ) => {
    const { salesChannelProductDetail } = getState();

    // 复用已有的数据
    if (
      !forceUpdate &&
      salesChannelProductDetail.loading === 'succeeded' &&
      salesChannelProductDetail.currentSalesChannelProductUuid ===
        salesChannelProductUuid &&
      salesChannelProductDetail.data
    ) {
      return salesChannelProductDetail.data;
    }

    try {
      const params = {
        ...langParams,
        [EnumSalesChannelMultiLanguage.multiLanguageInParams]: true,
        [EnumSalesChannelMultiLanguage.multiLanguageForceUpdate]: forceUpdate,
      };
      const response = await request_linkProductDetail(
        saleChannelUuid,
        salesChannelProductUuid,
        params,
      );
      const productData = response;
      if (!productData || typeof productData !== 'object') {
        throw new Error('Invalid response format');
      }
      return productData as ProductDetailData;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch product detail');
    }
  },
  {
    condition: ({ salesChannelProductUuid, currentLanguage }, { getState }) => {
      const { salesChannelProductDetail } = getState();

      const {
        loading = 'idle',
        currentLanguage: storedLanguage = '',
        currentSalesChannelProductUuid: storedUuid = '',
      } = salesChannelProductDetail || {};

      const isValidUuid = (uuid) => Boolean(uuid && uuid !== 'null');
      const inputUuidValid = isValidUuid(salesChannelProductUuid);
      const storedUuidValid = isValidUuid(storedUuid);

      if (inputUuidValid !== storedUuidValid) return true;

      if (inputUuidValid && storedUuid !== salesChannelProductUuid) return true;

      if (loading === 'pending' && storedLanguage === currentLanguage)
        return false;

      return true;
    },
  },
);

// slice
const salesChannelProductDetailSlice = createSlice({
  name: 'salesChannelProductDetail',
  initialState,
  reducers: {
    clearProductDetail: (state) => {
      state.data = null;
      state.loading = 'idle';
      state.error = null;
      state.currentSalesChannelProductUuid = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesChannelProductDetail.pending, (state, action) => {
        state.loading = 'pending';
        state.currentSalesChannelProductUuid =
          action.meta.arg.salesChannelProductUuid;
        state.error = null;
      })
      .addCase(fetchSalesChannelProductDetail.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchSalesChannelProductDetail.rejected, (state, action) => {
        state.loading = 'failed';
        state.data = null;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export const { clearProductDetail } = salesChannelProductDetailSlice.actions;
export default salesChannelProductDetailSlice.reducer;

export const selectProductDetailData = (state: {
  salesChannelProductDetail: SalesChannelProductDetailState;
}) => state.salesChannelProductDetail.data;
export const selectProductDetailLoading = (state: {
  salesChannelProductDetail: SalesChannelProductDetailState;
}) => state.salesChannelProductDetail.loading;
export const selectProductDetailError = (state: {
  salesChannelProductDetail: SalesChannelProductDetailState;
}) => state.salesChannelProductDetail.error;
